/**
 * Shared frozen empties for selectors that read an optional keyed bucket.
 *
 * A selector written `state.x.byKey[id] || []` looks harmless and is not.
 * `useSelector` compares its result by REFERENCE, so a fresh `[]` on every call
 * never compares equal: react-redux re-runs the selector whenever the store
 * snapshot changes, sees a "changed" value, and re-renders the component — on
 * every dispatch anywhere in the app, including ones that never touched this
 * slice.
 *
 * Wasted renders are the mild version. It becomes a request loop when the
 * component derives `useMemo` arrays from that result and hands them to a child
 * whose fetch effect depends on them: the identity change invalidates the memo
 * chain, the child refetches, and the next dispatch does it all again. That is
 * what kept the wind chart on the farm sensors page re-requesting its series —
 * `selectFormerDevices` returns the empty case for any farm with no released
 * devices, which is nearly all of them.
 *
 * One shared instance per shape fixes it for good. They are frozen so a caller
 * cannot mutate the value every other consumer is holding; pushing into a
 * selector result was already a bug, this just makes it a loud one.
 */
const FROZEN_EMPTY_ARRAY: readonly unknown[] = Object.freeze([]);
const FROZEN_EMPTY_OBJECT: Readonly<Record<string, never>> = Object.freeze({});

/** The one shared empty array, typed to the caller's element type. */
export function emptyArray<T>(): T[] {
  return FROZEN_EMPTY_ARRAY as T[];
}

/** The one shared empty record, typed to the caller's value type. */
export function emptyRecord<T>(): T {
  return FROZEN_EMPTY_OBJECT as T;
}
